import api from './api';

export const leadsService = {
  // Get all leads with filtering and pagination
  getLeads: async (params = {}) => {
    const response = await api.get('/leads/', { params });
    const data = response.data;

    // Ensure backward compatibility for existing leads
    if (data && data.results && Array.isArray(data.results)) {
      data.results = data.results.map(lead => {
        // Ensure arrays exist for assigned fields
        if (!Array.isArray(lead.assigned_states)) {
          lead.assigned_states = [];
        }
        if (!Array.isArray(lead.assigned_diseases)) {
          lead.assigned_diseases = [];
        }
        return lead;
      });
    }

    return data;
  },

  // Create a new lead
  createLead: async (leadData) => {
    const response = await api.post('/leads/', leadData);
    return response.data;
  },

  // Get lead by ID
  getLeadById: async (id) => {
    const response = await api.get(`/leads/${id}/`);
    const lead = response.data;

    // Ensure backward compatibility for existing leads
    if (lead) {
      // Ensure arrays exist for assigned fields
      if (!Array.isArray(lead.assigned_states)) {
        lead.assigned_states = [];
      }
      if (!Array.isArray(lead.assigned_diseases)) {
        lead.assigned_diseases = [];
      }
      if (!Array.isArray(lead.documents)) {
        lead.documents = [];
      }
    }

    return lead;
  },

  // Update lead
  updateLead: async (id, leadData) => {
    const response = await api.put(`/leads/${id}/`, leadData);
    return response.data;
  },

  // Update lead status with required fields
  updateLeadStatus: async (id, status) => {
    // First, get the current lead data
    const leadResponse = await api.get(`/leads/${id}/`);
    const lead = leadResponse.data;

    // Then update with the complete data including the new status
    const updateData = {
      name: lead.name,
      phone: lead.phone,
      email: lead.email || '',
      state: lead.state?.id || lead.state_id || lead.state,
      disease: lead.disease?.id || lead.disease_id || lead.disease,
      age: lead.age || '',
      address: lead.address || '',
      symptoms: lead.symptoms || '',
      medical_history: lead.medical_history || '',
      priority: lead.priority || 'medium',
      lead_source: lead.lead_source || lead.source || 'manual',
      notes: lead.notes || '',
      status: status
    };

    const response = await api.put(`/leads/${id}/`, updateData);
    return response.data;
  },

  // Delete lead
  deleteLead: async (id) => {
    await api.delete(`/leads/${id}/`);
  },

  // Assign lead to user
  assignLead: async (leadId, assignData) => {
    const payload = {
      lead_id: leadId,
      assignee_id: assignData.assigned_to,
      reason: assignData.reason || assignData.notes || ''
    };
    const response = await api.post('/auth/assign-lead/', payload);
    return response.data;
  },

  // Reassign lead to another user
  reassignLead: async (leadId, reassignData) => {
    const response = await api.post(`/leads/${leadId}/reassign/`, {
      assigned_to: reassignData.assigned_to,
      reason: reassignData.reason
    });
    return response.data;
  },

  // Get assignable users for a specific lead
  getAssignableUsers: async (leadId) => {
    const response = await api.get(`/auth/leads/${leadId}/assignable-users/`);
    return response.data;
  },

  // Bulk assign leads
  bulkAssignLeads: async (assignData) => {
    const response = await api.post('/leads/bulk-assign/', assignData);
    return response.data;
  },

  // Get lead comments
  getLeadComments: async (leadId) => {
    const response = await api.get(`/leads/${leadId}/comments/`);
    return response.data;
  },

  // Add comment to lead
  addLeadComment: async (leadId, commentData) => {
    const response = await api.post(`/leads/${leadId}/comments/`, commentData);
    return response.data;
  },

  // Legacy support
  addComment: async (leadId, comment) => {
    const response = await api.post(`/leads/${leadId}/comments/`, comment);
    return response.data;
  },

  // Get lead documents
  getLeadDocuments: async (leadId) => {
    const response = await api.get(`/leads/${leadId}/documents/`);
    const data = response.data;

    // Handle paginated response or ensure array return
    if (Array.isArray(data)) {
      return data;
    } else if (data && Array.isArray(data.results)) {
      return data.results;
    } else {
      console.warn('API returned unexpected document format:', data);
      return [];
    }
  },

  // Upload single document for lead (S3)
  uploadLeadDocument: async (leadId, file, documentType, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_name', file.name);
    formData.append('document_type', documentType);
    formData.append('description', description);

    const response = await api.post(`/leads/${leadId}/documents/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Bulk upload documents for lead (S3)
  bulkUploadDocuments: async (leadId, files, documentTypes, descriptions = []) => {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`files`, file);
      formData.append(`file_names`, file.name);
      formData.append(`document_types`, documentTypes[index]);
      formData.append(`descriptions`, descriptions[index] || '');
    });

    const response = await api.post(`/leads/${leadId}/documents/bulk-upload/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Download document with secure signed URL
  downloadDocument: async (documentId) => {
    try {
      const response = await api.get(`/leads/documents/${documentId}/download/`);

      if (response.data && response.data.download_url) {
        // Secure signed URL response
        return {
          download_url: response.data.download_url,
          filename: response.data.filename || 'document',
          expires_in: response.data.expires_in || 3600,
          is_s3_signed: true
        };
      }

      throw new Error('No download URL received from server');
    } catch (error) {
      console.error('Secure download error:', error);

      // Provide more specific error messages
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to download this document.');
      } else if (error.response?.status === 404) {
        throw new Error('Document not found or has been deleted.');
      } else if (error.response?.status === 401) {
        throw new Error('Please log in again to download documents.');
      } else {
        throw new Error('Failed to download document. Please try again or contact support.');
      }
    }
  },


  // Get document by ID
  getDocumentById: async (id) => {
    const response = await api.get(`/leads/documents/${id}/`);
    return response.data;
  },

  // Update document
  updateDocument: async (id, documentData) => {
    const response = await api.put(`/leads/documents/${id}/`, documentData);
    return response.data;
  },

  // Delete document
  deleteDocument: async (id) => {
    await api.delete(`/leads/documents/${id}/`);
  },

  // Get leads analytics
  getLeadsAnalytics: async () => {
    const response = await api.get('/leads/analytics/');
    return response.data;
  },

  // Get user performance
  getUserPerformance: async (userId = null) => {
    const params = userId ? { user_id: userId } : {};
    const response = await api.get('/leads/user-performance/', { params });
    return response.data;
  },

  // Get follow-up leads
  getFollowUpLeads: async (date = null) => {
    const params = date ? { date } : {};
    const response = await api.get('/leads/follow-ups/', { params });
    return response.data;
  },

  // Update lead with follow-up information
  updateLeadFollowUp: async (leadId, followUpData) => {
    const response = await api.patch(`/leads/${leadId}/`, followUpData);
    return response.data;
  },

  // Add follow-up comment to lead
  addFollowUpComment: async (leadId, commentData) => {
    const response = await api.post(`/leads/${leadId}/comments/`, commentData);
    return response.data;
  },

  // Bulk assign multiple leads for follow-up
  bulkAssignFollowUp: async (assignData) => {
    const response = await api.post('/leads/bulk-assign/', assignData);
    return response.data;
  },

  // Search leads with advanced filters
  searchLeads: async (searchParams) => {
    const response = await api.get('/leads/', { params: searchParams });
    return response.data;
  },

  // Lead Flow Control APIs
  pauseLeadFlow: async (userId, reason) => {
    const response = await api.post(`/accounts/lead-flow/${userId}/pause/`, {
      reason
    });
    return response.data;
  },

  resumeLeadFlow: async (userId) => {
    const response = await api.post(`/accounts/lead-flow/${userId}/resume/`);
    return response.data;
  },

  getAssignmentStats: async () => {
    const response = await api.get('/accounts/lead-flow/assignment-stats/');
    return response.data;
  },

  // Bulk document delete
  bulkDeleteDocuments: async (documentIds) => {
    const response = await api.delete('/leads/documents/bulk-delete/', {
      data: { document_ids: documentIds }
    });
    return response.data;
  },
};

// Lead status options
export const LEAD_STATUS = {
  NEW: 'new',
  WARM: 'warm',
  HOT: 'hot',
  IPD_DONE: 'ipd_done',
  OPD_DONE: 'opd_done',
  FOLLOWUP: 'followup',
  DNP: 'dnp',
  DEAD: 'dead',
  JUNK: 'junk',
  CONVERTED: 'converted',
};

export const LEAD_STATUS_LABELS = {
  [LEAD_STATUS.NEW]: 'New',
  [LEAD_STATUS.WARM]: 'Warm',
  [LEAD_STATUS.HOT]: 'Hot',
  [LEAD_STATUS.IPD_DONE]: 'IPD Done',
  [LEAD_STATUS.OPD_DONE]: 'OPD Done',
  [LEAD_STATUS.FOLLOWUP]: 'Follow-up',
  [LEAD_STATUS.DNP]: 'DNP',
  [LEAD_STATUS.DEAD]: 'Dead',
  [LEAD_STATUS.JUNK]: 'Junk',
  [LEAD_STATUS.CONVERTED]: 'Converted',
};

// Priority options
export const LEAD_PRIORITY = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

export const LEAD_PRIORITY_LABELS = {
  [LEAD_PRIORITY.HIGH]: 'High',
  [LEAD_PRIORITY.MEDIUM]: 'Medium',
  [LEAD_PRIORITY.LOW]: 'Low',
};

// Document types
export const DOCUMENT_TYPES = {
  AADHAR: 'aadhar',
  PAN: 'pan',
  INSURANCE: 'insurance',
  OT_NOTES: 'ot_notes',
  BILL: 'bill',
  DISCHARGE_SUMMARY: 'discharge_summary',
  CHECK: 'check',
  POLICY_PAPER: 'policy_paper',
  OTHERS: 'others',
};

export const DOCUMENT_TYPE_LABELS = {
  [DOCUMENT_TYPES.AADHAR]: 'Aadhar Card',
  [DOCUMENT_TYPES.PAN]: 'PAN Card',
  [DOCUMENT_TYPES.INSURANCE]: 'Insurance',
  [DOCUMENT_TYPES.OT_NOTES]: 'OT Notes',
  [DOCUMENT_TYPES.BILL]: 'Bill',
  [DOCUMENT_TYPES.DISCHARGE_SUMMARY]: 'Discharge Summary',
  [DOCUMENT_TYPES.CHECK]: 'Check',
  [DOCUMENT_TYPES.POLICY_PAPER]: 'Policy Paper',
  [DOCUMENT_TYPES.OTHERS]: 'Others',
};